import * as ko from 'knockout';
import { GameRecordsBase } from '../../shared/models/game-records-base';
import { NhlGameRecord } from './game-record';
import type { IGame } from '../../shared/interfaces/IGame';
import { SelectionType } from '../../shared/enums/selection-type';
import type { GameRecordOptions } from '../../shared/models/game-record-base';

/**
 * NHL GameRecords.
 * - Adds Regulation, Overtime, Shootout win/loss buckets.
 * - Adds LossesRegulation record bucket.
 * - Adds Points, ROW, GP, PointsPct computed observables.
 * - Categorizes wins/losses by SelectionType in initSubscribers hooks.
 */
export class NhlGameRecords extends GameRecordsBase {
  Regulation: NhlGameRecord;
  Overtime: NhlGameRecord;
  Shootout: NhlGameRecord;
  LossesRegulation: NhlGameRecord;
  Points: KnockoutComputed<number>;
  ROW: KnockoutComputed<number>;
  GP: KnockoutComputed<number>;
  PointsPct: KnockoutComputed<number>;

  protected override get recordOptions(): Partial<GameRecordOptions> {
    return { includeTiesInDenominator: false, zeroGamesValue: 0, zeroPctValue: -1 };
  }

  constructor(
    Wins: KnockoutObservableArray<IGame>,
    Losses: KnockoutObservableArray<IGame>,
    Ties: KnockoutObservableArray<IGame>,
    teamName: string,
    fakeObs: KnockoutObservable<any>
  ) {
    super(Wins, Losses, Ties, teamName, fakeObs);

    this.Regulation = new NhlGameRecord(fakeObs);
    this.Overtime = new NhlGameRecord(fakeObs);
    this.Shootout = new NhlGameRecord(fakeObs);
    this.LossesRegulation = new NhlGameRecord(fakeObs);

    this.Points = ko.computed(() => {
      const rawWins = this.AllRecord.Wins();
      const shootoutLosses = this.Shootout.Losses();
      const overtimeLosses = this.Overtime.Losses();
      return rawWins * 2 + shootoutLosses + overtimeLosses;
    });

    this.ROW = ko.computed(() => {
      const allWins = this.AllRecord.Wins();
      const soWins = this.Shootout.Wins();
      return allWins - soWins;
    });

    this.GP = ko.computed(() => {
      return this.Wins().length + this.Losses().length;
    });

    this.PointsPct = ko.computed(() => {
      const gp = this.GP();
      if (gp === 0) return 0;
      return this.Points() / (gp * 2);
    });
  }

  protected override onWinAdded(item: IGame): void {
    if (item.selectionType && item.selectionType()) {
      switch (item.selectionType()) {
        case SelectionType.AWAY_WIN_OVERTIME:
        case SelectionType.HOME_WIN_OVERTIME:
          this.Overtime.Wins.increment();
          break;
        case SelectionType.AWAY_WIN_SHOOTOUT:
        case SelectionType.HOME_WIN_SHOOTOUT:
          this.Shootout.Wins.increment();
          break;
        case SelectionType.HOME_WIN:
        case SelectionType.AWAY_WIN:
          this.Regulation.Wins.increment();
          break;
      }
    }
  }

  protected override onLossAdded(item: IGame): void {
    if (item.selectionType && item.selectionType()) {
      switch (item.selectionType()) {
        case SelectionType.AWAY_WIN_OVERTIME:
        case SelectionType.HOME_WIN_OVERTIME:
          this.Overtime.Losses.increment();
          break;
        case SelectionType.AWAY_WIN_SHOOTOUT:
        case SelectionType.HOME_WIN_SHOOTOUT:
          this.Shootout.Losses.increment();
          break;
        case SelectionType.HOME_WIN:
        case SelectionType.AWAY_WIN:
          this.Regulation.Losses.increment();
          break;
      }
    }
  }

  override resetWins(): void {
    super.resetWins();
    this.Overtime.resetWins();
    this.Shootout.resetWins();
    this.Regulation.resetWins();
  }

  override resetLosses(): void {
    super.resetLosses();
    this.Overtime.resetLosses();
    this.Shootout.resetLosses();
    this.Regulation.resetLosses();
    this.LossesRegulation.resetLosses();
  }
}
